import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { environmentWabaService } from '../services/environmentWabaService';

const EnvironmentContext = createContext();

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within EnvironmentProvider');
  }
  return context;
};

export const EnvironmentProvider = ({ children }) => {
  const { authenticatedFetch } = useAuth();
  const { selectedOrganization } = useOrganization();
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // WABA connection status for environments (Map: envId -> wabaConnection)
  const [wabaConnections, setWabaConnections] = useState({});

  const API_URL = process.env.REACT_APP_API_URL;

  // The organisation whose environments we are currently loading. A response for any other one
  // arrived after the user switched away (or signed out and in as somebody else) and must be
  // dropped: kept, the selection effect below picked the PREVIOUS organisation's first
  // environment and saved it under the new one, so the next WABA connect was sent with the new
  // org's id and the old org's environment (2026-09-27).
  const loadingOrgRef = useRef(null);

  const loadEnvironments = async (orgId) => {
    loadingOrgRef.current = orgId;
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/organisations/${orgId}/environments`);

      if (!response.ok) {
        throw new Error('Failed to load environments');
      }

      const data = await response.json();
      if (loadingOrgRef.current !== orgId) {
        return;
      }
      setEnvironments(data);

      // Load WABA connections for all environments
      await loadWabaConnectionsForEnvironments(orgId, data);
    } catch (err) {
      console.error('Error loading environments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadWabaConnectionsForEnvironments = async (orgId, envs) => {
    if (!orgId || !envs || envs.length === 0) {
      return;
    }

    try {
      // Load WABA connections for all environments in parallel
      const connectionPromises = envs.map(env =>
        environmentWabaService.getConnection(orgId, env.id, authenticatedFetch)
          .then(connection => ({ envId: env.id, connection }))
          .catch(err => {
            console.warn(`Failed to load WABA for environment ${env.id}:`, err);
            return { envId: env.id, connection: null };
          })
      );

      const results = await Promise.all(connectionPromises);

      // Update wabaConnections map
      const connections = {};
      results.forEach(({ envId, connection }) => {
        connections[envId] = connection;
      });

      setWabaConnections(connections);
    } catch (err) {
      console.error('Error loading WABA connections:', err);
    }
  };

  const loadWabaConnection = async (envId) => {
    if (!selectedOrganization) {
      return null;
    }

    try {
      const connection = await environmentWabaService.getConnection(
        selectedOrganization.id,
        envId,
        authenticatedFetch
      );

      setWabaConnections(prev => ({
        ...prev,
        [envId]: connection
      }));

      return connection;
    } catch (err) {
      console.error(`Error loading WABA for environment ${envId}:`, err);
      return null;
    }
  };

  const connectWabaToEnvironment = async (envId, wabaData, options = {}) => {
    if (!selectedOrganization) {
      throw new Error('No organization selected');
    }

    setLoading(true);
    setError(null);
    try {
      const connection = await environmentWabaService.connectWaba(
        selectedOrganization.id,
        envId,
        wabaData,
        authenticatedFetch,
        options
      );

      // Update local state
      setWabaConnections(prev => ({
        ...prev,
        [envId]: connection
      }));

      return connection;
    } catch (err) {
      console.error('Error connecting WABA:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshWabaToken = async (envId, accessToken, expiresIn) => {
    if (!selectedOrganization) {
      throw new Error('No organization selected');
    }

    setLoading(true);
    setError(null);
    try {
      const connection = await environmentWabaService.refreshToken(
        selectedOrganization.id,
        envId,
        accessToken,
        expiresIn,
        authenticatedFetch
      );

      // Update local state
      setWabaConnections(prev => ({
        ...prev,
        [envId]: connection
      }));

      return connection;
    } catch (err) {
      console.error('Error refreshing WABA token:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const getWabaConnection = (envId) => {
    return wabaConnections[envId] || null;
  };

  const selectEnvironment = (envId) => {
    const env = environments.find(e => e.id === envId);
    if (env && selectedOrganization) {
      setSelectedEnvironment(env);
      localStorage.setItem(`selectedEnvironmentId_${selectedOrganization.id}`, envId);
    }
  };

  const createEnvironment = async (orgId, name) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments`,
        {
          method: 'POST',
          body: JSON.stringify({ name })
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can create environments');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create environment');
      }

      const newEnv = await response.json();
      setEnvironments(prev => [...prev, newEnv]);
      setSelectedEnvironment(newEnv);
      localStorage.setItem(`selectedEnvironmentId_${orgId}`, newEnv.id);

      return newEnv;
    } catch (err) {
      console.error('Error creating environment:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateEnvironment = async (orgId, envId, name) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ name })
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can update environments');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update environment');
      }

      const updatedEnv = await response.json();
      setEnvironments(prev => prev.map(e => e.id === envId ? updatedEnv : e));

      if (selectedEnvironment?.id === envId) {
        setSelectedEnvironment(updatedEnv);
      }

      return updatedEnv;
    } catch (err) {
      console.error('Error updating environment:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const archiveEnvironment = async (orgId, envId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}/archive`,
        {
          method: 'POST'
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can archive environments');
      }

      if (!response.ok) {
        throw new Error('Failed to archive environment');
      }

      const archivedEnv = await response.json();
      setEnvironments(prev => prev.map(e => e.id === envId ? archivedEnv : e));

      if (selectedEnvironment?.id === envId) {
        const activeEnvs = environments.filter(e => !e.isArchived && e.id !== envId);
        setSelectedEnvironment(activeEnvs.length > 0 ? activeEnvs[0] : null);
        if (activeEnvs.length > 0) {
          localStorage.setItem(`selectedEnvironmentId_${orgId}`, activeEnvs[0].id);
        } else {
          localStorage.removeItem(`selectedEnvironmentId_${orgId}`);
        }
      }

      return archivedEnv;
    } catch (err) {
      console.error('Error archiving environment:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const unarchiveEnvironment = async (orgId, envId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}/unarchive`,
        {
          method: 'POST'
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can unarchive environments');
      }

      if (!response.ok) {
        throw new Error('Failed to unarchive environment');
      }

      const unarchivedEnv = await response.json();
      setEnvironments(prev => prev.map(e => e.id === envId ? unarchivedEnv : e));

      return unarchivedEnv;
    } catch (err) {
      console.error('Error unarchiving environment:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteEnvironment = async (orgId, envId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/organisations/${orgId}/environments/${envId}`,
        {
          method: 'DELETE'
        }
      );

      if (response.status === 403) {
        throw new Error('Only organization owners can delete environments');
      }

      if (!response.ok) {
        throw new Error('Failed to delete environment');
      }

      setEnvironments(prev => prev.filter(e => e.id !== envId));

      if (selectedEnvironment?.id === envId) {
        const remainingEnvs = environments.filter(e => e.id !== envId);
        setSelectedEnvironment(remainingEnvs.length > 0 ? remainingEnvs[0] : null);
        if (remainingEnvs.length > 0) {
          localStorage.setItem(`selectedEnvironmentId_${orgId}`, remainingEnvs[0].id);
        } else {
          localStorage.removeItem(`selectedEnvironmentId_${orgId}`);
        }
      }
    } catch (err) {
      console.error('Error deleting environment:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshEnvironments = async () => {
    if (selectedOrganization) {
      await loadEnvironments(selectedOrganization.id);
    }
  };

  useEffect(() => {
    if (selectedOrganization?.id) {
      setEnvironments([]);
      setSelectedEnvironment(null);
      loadEnvironments(selectedOrganization.id);
    } else {
      setEnvironments([]);
      setSelectedEnvironment(null);
    }
  }, [selectedOrganization?.id]);

  useEffect(() => {
    if (environments.length > 0 && selectedOrganization) {
      const savedEnvId = localStorage.getItem(`selectedEnvironmentId_${selectedOrganization.id}`);

      if (savedEnvId) {
        const savedEnv = environments.find(e => e.id === savedEnvId && !e.isArchived);
        if (savedEnv) {
          setSelectedEnvironment(savedEnv);
          return;
        }
      }

      const activeEnvs = environments.filter(e => !e.isArchived);
      if (activeEnvs.length > 0) {
        setSelectedEnvironment(activeEnvs[0]);
        localStorage.setItem(`selectedEnvironmentId_${selectedOrganization.id}`, activeEnvs[0].id);
      }
    }
  }, [environments, selectedOrganization]);

  const value = {
    environments,
    selectedEnvironment,
    loading,
    error,
    loadEnvironments,
    selectEnvironment,
    createEnvironment,
    updateEnvironment,
    archiveEnvironment,
    unarchiveEnvironment,
    deleteEnvironment,
    refreshEnvironments,
    // WABA connection management
    wabaConnections,
    getWabaConnection,
    loadWabaConnection,
    connectWabaToEnvironment,
    refreshWabaToken
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};
